import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation, useSearchParams } from 'react-router-dom';
import { getCatalog } from '../api/client';
import { cacheImages } from '../lib/cacheImages';
import SiteHeader from './shared/SiteHeader';
import { uiLabels } from '../lib/uiLabels';
import { usePageMeta } from '../lib/usePageMeta';

const IMAGE_BASE = (import.meta.env.VITE_IMAGE_URL ?? '').replace(/\/$/, '');

const PAGE_TITLES = {
  1: 'Ejercicios para Estudiantes',
  4: 'Ejercicios para la Comunidad',
  2: 'Exercises for Students',
};

function imageUrl(path) {
  if (!path) return null;
  const normalized = String(path).replace(/^~\//, '/');
  if (normalized.startsWith('http')) return normalized;
  if (!IMAGE_BASE) return normalized.startsWith('/') ? normalized : `/${normalized}`;
  return `${IMAGE_BASE}${normalized.startsWith('/') ? normalized : `/${normalized}`}`;
}

function tareaImage(t) {
  if (t.rutaImagen) return imageUrl(t.rutaImagen);
  // Miniaturas bundleadas en el hosting propio (public/images), no en el sitio viejo:
  // al cambiar el DNS de labpsi.mdp.edu.ar a Firebase, /images/t_{id}.png sigue resolviendo.
  return `/images/t_${t.ID}.png`;
}

/** Franja decorativa bajo el header (barra negra). No usar 01.png: es la cabecita. */
function stripImage() {
  return '/assets/img/04.png';
}

export default function Tareas() {
  const { lenguajeId, categoriaId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const testMode = searchParams.get('test') === '1';
  const lid = Number(lenguajeId);
  const cid = Number(categoriaId);
  const isCogni = lid === 4;
  usePageMeta(`${location.state?.categoria?.Descripcion ?? PAGE_TITLES[lid] ?? 'Tareas'} | LABPSI`);

  const [resolved, setResolved] = useState(() => ({
    categoria: location.state?.categoria,
    lenguaje: location.state?.lenguaje,
  }));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUp, setShowUp] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getCatalog(lid, testMode)
      .then((data) => {
        const cat = data.categorias?.find((c) => c.ID === cid) ?? null;
        setResolved({ categoria: cat, lenguaje: data.lenguaje });
        const urls = (cat?.tareas ?? []).map(tareaImage).filter(Boolean);
        cacheImages(urls);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [lid, cid, testMode]);

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
    setShowUp(false);

    const update = () => {
      const scrollY = window.scrollY || 0;
      const viewH = window.innerHeight;
      const docH = document.documentElement.scrollHeight;
      const canScroll = docH > viewH + 150;
      setShowUp(canScroll && scrollY + viewH >= docH - 80);
    };
    window.addEventListener('scroll', update, { passive: true });
    requestAnimationFrame(() => requestAnimationFrame(update));
    return () => window.removeEventListener('scroll', update);
  }, [lid, cid]);

  const goBack = () => navigate(`/lenguaje/${lenguajeId}${testMode ? '?test=1' : ''}`);

  if (loading && !resolved.categoria) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-slate-600">Cargando...</p>
      </div>
    );
  }

  if (error && !resolved.categoria) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <p className="text-red-600">{error}</p>
        <button type="button" onClick={goBack} className="ml-4 text-slate-600 underline bg-transparent border-0 cursor-pointer">
          Volver a categorías
        </button>
      </div>
    );
  }

  const { categoria, lenguaje } = resolved;
  if (!categoria) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div>
          <p className="text-slate-600">No hay datos de categoría.</p>
          <button type="button" onClick={goBack} className="mt-4 text-slate-600 underline bg-transparent border-0 cursor-pointer">
            Volver a categorías
          </button>
        </div>
      </div>
    );
  }

  const tareas = [...(categoria.tareas ?? [])].sort((a, b) =>
    String(a.Descripcion || '').localeCompare(String(b.Descripcion || ''), 'es')
  );
  const pageTitle = PAGE_TITLES[lenguaje?.ID] ?? PAGE_TITLES[lid] ?? lenguaje?.Descripcion ?? 'Ejercicios';
  const btnText = (lenguaje?.Textobtn || 'INGRESAR').toUpperCase();
  const themeClass = isCogni ? 'cogni' : 'pedag';
  const labels = uiLabels(lid);
  const imgDef = '/assets/img/imgdef.png';

  const goToTarea = (t) =>
    navigate(`/lenguaje/${lenguajeId}/categoria/${categoriaId}/tarea/${t.ID}${testMode ? '?test=1' : ''}`, {
      state: { tarea: t, categoria, lenguaje },
    });

  const goDown = () => {
    window.scrollBy({ top: Math.max(300, window.innerHeight * 0.7), behavior: 'smooth' });
  };
  const goUp = () => {
    window.scrollBy({ top: -Math.max(300, window.innerHeight * 0.7), behavior: 'smooth' });
  };

  return (
    <div className={`page-tipo-test page-detalle-test ${themeClass}`}>
      <div className="navbar-wrapper">
        <SiteHeader lenguajeId={lid} />

        <div className="tipo-test-title-row">
          <button type="button" className="btn-back" onClick={goBack}>
            {labels.back}
          </button>
          <h1 className="title-pag">
            <button
              type="button"
              className="title-page-content title-pag-btn"
              onClick={goBack}
            >
              {pageTitle}
            </button>
          </h1>
        </div>
      </div>

      <img
        className="detalle-strip"
        src={stripImage()}
        alt=""
      />

      <div className="marketing">
        <div className="sections-container">
          <h2 className="detalle-cat-title">{categoria.Descripcion}</h2>

          {categoria.marcoteorico && (
            <p className="detalle-marco">{categoria.marcoteorico}</p>
          )}

          <hr className="detalle-divider" />

          <div className="tipo-test-grid">
            {tareas.map((t) => (
              <a
                key={t.ID}
                href={`/lenguaje/${lenguajeId}/categoria/${categoriaId}/tarea/${t.ID}${testMode ? '?test=1' : ''}`}
                className="test-item sub-test-item"
                onClick={(e) => {
                  e.preventDefault();
                  goToTarea(t);
                }}
              >
                <div className={`item-content ${isCogni ? 'ej-cogni' : 'ej-pedag'}`}>
                  <img
                    className="img-circle"
                    src={tareaImage(t) || imgDef}
                    alt=""
                    decoding="async"
                    loading="lazy"
                    onError={(e) => {
                      const el = e.currentTarget;
                      if (el.dataset.fallback === '1') {
                        el.onerror = null;
                        // Estudiantes: lápiz/papel; Comunidad: logo (cabecita) como último recurso
                        el.src = isCogni ? '/assets/img/logo.png' : '/assets/img/imgdef2.png';
                        return;
                      }
                      el.dataset.fallback = '1';
                      // Evitar imgdef.png (cabecita con engranajes) en Estudiantes
                      el.src = isCogni ? '/assets/img/imgdef.png' : '/assets/img/imgdef2.png';
                    }}
                  />
                  <div className="vertical-center">{t.Descripcion}</div>
                  {t.observaciones ? (
                    <p className="detalle-card-obs">{t.observaciones}</p>
                  ) : null}
                  <span className="btn-ingresar">{btnText}</span>
                </div>
              </a>
            ))}
          </div>

          {categoria.observaciones ? (
            <div className="detalle-observaciones">
              <hr className="detalle-divider" />
              <div className="detalle-obs-body">
                <div>
                  <h2 className="detalle-obs-title">
                    Observa<span className="text-muted">ciones</span>
                  </h2>
                  <p className="detalle-marco">{categoria.observaciones}</p>
                </div>
                {(categoria.rutaImagen || cid) ? (
                  <img
                    className="detalle-obs-img"
                    src={imageUrl(categoria.rutaImagen) || imageUrl(`/images/e_${cid}.png`)}
                    alt=""
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="tipo-test-footer">
            <button
              type="button"
              className="icono-home-new"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              <img src="/assets/img/flecha-volver.png" alt="" />
              <br />{labels.backTopLine1}
              <br />{labels.backTopLine2}
            </button>
          </div>
        </div>
      </div>

      {!showUp && (
        <button type="button" className="icono-abajo" onClick={goDown} aria-label="Desplazar hacia abajo">
          <img src="/assets/img/abajoNew.png" alt="" />
        </button>
      )}
      {showUp && (
        <button type="button" className="icono-arriba" onClick={goUp} aria-label="Desplazar hacia arriba">
          <img src="/assets/img/arribaNew.png" alt="" />
        </button>
      )}
    </div>
  );
}
